<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="p-3" v-if="operation">
      <table class="table table-striped border-light">
        <thead>
        <tr>
          <th colspan="2">
            <h5 class="mb-0 text-center text-capitalize-first">
              {{ helper.trans('labels.operation') }}
            </h5>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <th class="text-capitalize-first">{{ helper.trans('labels.id') }}</th>
          <td>{{ operation.id }} <copy-button :value="operation.id"></copy-button></td>
        </tr>
        <tr>
          <th class="text-capitalize-first">{{ helper.trans('labels.date') }}</th>
          <td>{{ dateHelper.format(operation.createdAt) }}</td>
        </tr>
        <tr>
          <th class="text-capitalize-first">{{ helper.trans('labels.type') }}</th>
          <td>{{ helper.trans(`operation_type.${operation.operationType}`) }}</td>
        </tr>
        <tr>
          <th class="text-capitalize-first">{{ helper.trans('labels.sum') }}</th>
          <td>
            <span :class="{'text-success': operation.transactionType === TransactionType.CREDIT, 'text-danger': operation.transactionType !== TransactionType.CREDIT}">
              {{ operation.transactionType === TransactionType.CREDIT ? "+" : '-' }}{{ numberFormatter.formatMoney(operation.sum) }}
              <copy-button :value="numberFormatter.formatNumber(operation.sum)"></copy-button>
          </span>
          </td>
        </tr>
        <tr>
          <th class="text-capitalize-first">{{ helper.trans('labels.balance_before') }}</th>
          <td>{{ numberFormatter.formatMoney(operation.balanceBefore) }}</td>
        </tr>
        <tr>
          <th class="text-capitalize-first">{{ helper.trans('labels.balance_after') }}</th>
          <td>{{ numberFormatter.formatMoney(operation.balanceAfter) }}</td>
        </tr>
        </tbody>
      </table>

      <div v-if="invoiceDeal">
        <h5 class="mb-0 text-center text-capitalize-first">{{ helper.trans('labels.invoice_deal') }}</h5>
        <InvoiceDealDetailsTable :deals="[invoiceDeal]"></InvoiceDealDetailsTable>
      </div>

      <div v-if="invoice">
        <InvoiceDetailsTable :invoice="invoice"></InvoiceDetailsTable>
      </div>

      <div v-if="deposit">
        <table class="table table-striped border-light">
          <thead>
          <tr>
            <th colspan="2">
              <h5 class="mb-0 text-center text-capitalize-first">
                {{helper.trans('labels.deposit_tx')}}
              </h5>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.sum') }}</th>
            <td>{{ numberFormatter.formatMoney(deposit.sum) }}
              <copy-button :value="deposit.sum.amount"></copy-button>
              <small class="text-secondary ms-2" v-if="deposit.creditedSum.currency !== deposit.sum.currency">
                {{ numberFormatter.formatMoney(deposit.creditedSum) }}
                <copy-button :value="deposit.creditedSum.amount"></copy-button>
              </small>
            </td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.address.usdt') }}</th>
            <td>{{ deposit.walletAddress }}
              <copy-button :value="deposit.walletAddress"></copy-button>
            </td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.from_address.usdt') }}</th>
            <td>{{ deposit.fromAddress }}
              <copy-button :value="deposit.fromAddress"></copy-button>
            </td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.tx_id') }}</th>
            <td>
              {{ deposit.txId.substring(0, 16) }}...
              <copy-button :value="deposit.txId"></copy-button>
              <a :href="`https://tronscan.org/#/transaction/${deposit.txId}`" target="_blank" class="ms-2">
                <i class="fa fa-external-link"></i>
              </a>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-if="withdrawal">
        <table class="table table-striped border-light">
          <thead>
          <tr>
            <th colspan="2">
              <h5 class="mb-0 text-center text-capitalize-first">
                {{helper.trans('labels.withdraw_order')}}
              </h5>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.id') }}</th>
            <td>{{ withdrawal.id }} <copy-button :value="withdrawal.id"></copy-button></td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.date') }}</th>
            <td>{{ dateHelper.format(withdrawal.createdAt) }}</td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.status') }}</th>
            <td>
              {{ helper.trans(`withdrawal_status.${withdrawal.status}`) }}
            </td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.sum') }}</th>
            <td>{{ numberFormatter.formatMoney(withdrawal.sum) }}
              <copy-button :value="withdrawal.sum.amount"></copy-button>
              <small class="text-secondary ms-2" v-if="withdrawal.debitedSum.currency !== withdrawal.sum.currency">
                {{ numberFormatter.formatMoney(withdrawal.debitedSum) }}
                <copy-button :value="withdrawal.debitedSum.amount"></copy-button>
              </small>
            </td>
          </tr>
          <tr v-if="withdrawal.fee">
            <th class="text-capitalize-first">{{ helper.trans('labels.fee') }}</th>
            <td>{{ numberFormatter.formatMoney(withdrawal.fee) }}
              <copy-button :value="withdrawal.fee.amount"></copy-button>
              <small class="text-secondary ms-2" v-if="withdrawal.debitedFee.currency !== withdrawal.fee.currency">
                {{ numberFormatter.formatMoney(withdrawal.debitedFee) }}
                <copy-button :value="withdrawal.debitedFee.amount"></copy-button>
              </small>
            </td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.address.usdt') }}</th>
            <td>{{ withdrawal.address }}
              <copy-button :value="withdrawal.address"></copy-button>
            </td>
          </tr>
          <tr>
            <th class="text-capitalize-first">{{ helper.trans('labels.tx_id') }}</th>
            <td>
              <div v-if="withdrawal.txId">
                {{ withdrawal.txId.substring(0, 16) }}...
                <copy-button :value="withdrawal.txId"></copy-button>
                <a :href="`https://tronscan.org/#/transaction/${withdrawal.txId}`" target="_blank" class="ms-2">
                  <i class="fa fa-external-link"></i>
                </a>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-if="linkedAccount">
        <table class="table table-striped border-light">
          <thead>
          <tr>
            <th colspan="2">
              <h5 class="mb-0 text-center text-capitalize-first">
                {{ helper.trans(`operation_type.${operation.operationType}`) }}
              </h5>
            </th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <th class="text-capitalize-first">{{ operation.transactionType === TransactionType.DEBIT ? helper.trans('labels.to_account') : helper.trans('labels.from_account') }}</th>
            <td>
              <UserNameLabel v-if="linkedSystemAccount" show-badge :user="systemUser"></UserNameLabel>
              <UserNameLabel v-if="linkedTraderAccount" show-badge :user="linkedTraderAccount.user"></UserNameLabel>
              <UserNameLabel v-if="linkedUserAccount" show-badge :user="linkedUserAccount.user"></UserNameLabel>
              <div v-if="linkedMerchantStoreAccount" class="d-flex flex-row align-items-center">
                <div>
                  <UserNameLabel show-badge :user="linkedMerchantStoreAccount.user"></UserNameLabel>
                  <div class="d-flex" style="clear: both">{{linkedMerchantStoreAccount?.storeName }}</div>
                </div>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-if="deal">
        <TradeDealDetailsTable :deal="deal"></TradeDealDetailsTable>
      </div>
    </div>

  </LoaderWrapper>
</template>

<script lang="ts">

import {defineComponent, Prop, PropType, ref, watch} from 'vue'
import {helper} from "@/service/CommonHelper";
import {reactive, toRefs} from "@vue/reactivity";
import AccountOperationDTO from "@/models/AccountOperationDTO";
import APIClient from "@/service/APIClient";
import TraderDealDTO from "@/models/TraderDealDTO";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import {TransactionType} from "@/models/enum/TransactionType";
import CopyButton from "@/components/CopyButton.vue";
import {useStore} from "vuex";
import {OperationType} from "@/models/enum/OperationType";
import InvoiceDTO from "@/models/InvoiceDTO";
import InvoiceDealDTO from "@/models/InvoiceDealDTO";
import InvoiceDetailsTable from "@/components/InvoiceDetailsTable.vue";
import InvoiceDealDetailsTable from "@/components/InvoiceDealDetailsTable.vue";
import DepositDTO from "@/models/DepositDTO";
import {UserRole} from "@/models/enum/UserRole";
import WithdrawalDTO from "@/models/WithdrawalDTO";
import TradeDealDetailsTable from "@/components/TradeDealDetailsTable.vue";
import UserAccountDTO from "@/models/UserAccountDTO";
import MerchantStoreAccountDTO from "@/models/MerchantStoreAccountDTO";
import TraderAccountDTO from "@/models/TraderAccountDTO";
import PaginationDTO from "@/models/PaginationDTO";
import UserNameLabel from "@/components/UserNameLabel.vue";
import AccountDTO from "@/models/AccountDTO";
import {SystemUser} from "@/models/UserDTO";

const api = new APIClient();

export default defineComponent({
  components: {
    UserNameLabel,
    LoaderWrapper,
    CopyButton,
    InvoiceDetailsTable,
    InvoiceDealDetailsTable,
    TradeDealDetailsTable
  },
  props: {
    operation: {
      type: Object as PropType<AccountOperationDTO>,
      default: null
    } as Prop<AccountOperationDTO>
  },
  setup(props) {
    const store = useStore()

    helper.initCopyHints();

    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)

    const state = reactive({
      deal: null as TraderDealDTO|null,
      deposit: null as DepositDTO|null,
      withdrawal: null as WithdrawalDTO|null,
      invoice: null as InvoiceDTO|null,
      invoiceDeal: null as InvoiceDealDTO|null,
      linkedAccount: null as UserAccountDTO|MerchantStoreAccountDTO|TraderAccountDTO|AccountDTO|null,
      linkedTraderAccount: null as TraderAccountDTO|null,
      linkedMerchantStoreAccount: null as  MerchantStoreAccountDTO|null,
      linkedUserAccount: null as UserAccountDTO|null,
      linkedSystemAccount: null as AccountDTO|null,
      systemUser: SystemUser
    });

    watch(() => props.operation, async () => {
      state.deal = null;
      state.invoiceDeal = null;
      state.invoice = null;
      state.withdrawal = null;
      state.deposit = null;
      state.linkedAccount = null;
      state.linkedSystemAccount = null;
      state.linkedUserAccount = null;
      state.linkedMerchantStoreAccount = null;
      state.linkedTraderAccount = null;

      if (props.operation?.dealId) {
        if (store.getters.isTrader || store.getters.isTeamLead || store.getters.isTraderOperator) {
          loader.value?.show()
          state.deal = await api.deal(props.operation.dealId!);
          loader.value?.hide()
        } else if (store.getters.isMerchant || store.getters.isReferral || store.getters.isMerchantOperator) {
          loader.value?.show()
          await Promise.all([
            api.invoiceDeal(props.operation.dealId!)
              .then(resp => state.invoiceDeal = resp),
            api.invoices({offset: 0, limit: 1}, {}, {dealId: props.operation.dealId!})
              .then((resp) => {
                if (resp.items.length === 1) {
                  state.invoice = resp.items[0];
                }
              })
          ])
          loader.value?.hide()
        } else if (store.getters.isAdmin) {
          loader.value?.show()
          await Promise.all([
            api.deal(props.operation.dealId!)
              .then(resp => state.deal = resp),
            api.invoices({offset: 0, limit: 1}, {}, {dealId: props.operation.dealId!})
              .then((resp) => {
                if (resp.items.length === 1) {
                  state.invoice = resp.items[0];
                }
              })
          ])
          loader.value?.hide()
        }
      }

      if (props.operation?.depositId && store.getters.hasRole([UserRole.ADMIN, UserRole.TRADER, UserRole.OPERATOR, UserRole.MERCHANT_OPERATOR, UserRole.MERCHANT])) {
        loader.value?.show()
        state.deposit = await api.call.deposits.get(props.operation.depositId!);
        loader.value?.hide()
      }

      if (props.operation?.withdrawalId) {
        loader.value?.show()
        state.withdrawal = await api.withdrawal(props.operation.withdrawalId!);
        loader.value?.hide()
      }

      if (props.operation?.linkedAccountId && store.getters.hasRole([UserRole.ADMIN, UserRole.OPERATOR])) {
        loader.value?.show()

        let accountId = props.operation?.linkedAccountId;

        if (accountId === '00000000-0000-0000-0000-000000000000') {
          state.linkedAccount = await api.accountsGet('00000000-0000-0000-0000-000000000000')
          state.linkedSystemAccount = state.linkedAccount
        }

        if (!state.linkedAccount) {
          for (const account of (await api.traderAccounts({
            offset: 0,
            limit: 1
          } as PaginationDTO, {ids: [accountId]})).items) {
            state.linkedAccount = account
            state.linkedTraderAccount = account
          }
        }

        if (!state.linkedAccount) {
          for (const account of (await api.merchantStoreAccounts({offset:0, limit: 1} as PaginationDTO, {ids: [accountId]})).items) {
            state.linkedAccount = account
            state.linkedMerchantStoreAccount = account
          }
        }

        if (!state.linkedAccount) {
          for (const account of (await api.userAccounts({offset:0, limit: 1} as PaginationDTO, {ids: [accountId]})).items) {
            state.linkedAccount = account
            state.linkedUserAccount = account
          }
        }

        loader.value?.hide()
      }
    })

    return {
      ...toRefs(state),
      loader,
      dateHelper,
      numberFormatter,
      TransactionType,
      helper
    }
  }
})
</script>
